import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { knowledgeBaseApi } from 'api/knowledgeBase';
import { DialogConfirm, DialogConfirmExtendProps } from 'components/_new/Dialog';
import { useGlobal } from 'contexts/globalContext';
import { useScope } from 'contexts/scopeContext';

type RemoveArticleDialogProps = DialogConfirmExtendProps & { articleId: number | null };

export const RemoveArticleDialog = ({ articleId, ...dialogProps }: RemoveArticleDialogProps) => {
	const { t } = useTranslation();

	const { toastRef } = useGlobal();
	const { currentProgram: data } = useScope();

	const { data: knowledgeBaseArticle } = useQuery(
		['KNOWLEDGE_BASE_PP_ARTICLE', { programId: Number(data?.id), articleId }],
		() => knowledgeBaseApi.program.getProgramArticleById(Number(data?.id), Number(articleId)),
		{
			enabled: Boolean(dialogProps.visible) && Boolean(articleId),
		}
	);

	const { data: knowledgeBaseArticleShow } = useQuery(
		['KNOWLEDGE_BASE_PP_ARTICLE_SHOW', { programId: Number(data?.id), articleId }],
		() => knowledgeBaseApi.program.getProgramArticleByIdShow(Number(data?.id), Number(articleId)),
		{
			enabled: Boolean(dialogProps.visible) && Boolean(articleId),
		}
	);

	const { mutateAsync: removeArticleMutate } = useMutation(
		() => knowledgeBaseApi.program.removeProgramArticle(Number(data?.id), Number(articleId)),
		{
			onSuccess: () => {
				toastRef?.current?.show({
					severity: 'success',
					life: 3000,
					summary: t('misc.success'),
					detail: t('knowledgeBase.programPanel.articleRemoved'),
				});
				dialogProps?.onHide?.();
			},
		}
	);

	const title = knowledgeBaseArticleShow?.articleContent.title || '';

	return (
		<DialogConfirm
			message={t('knowledgeBase.programPanel.confirmRemoveArticle', { title })}
			{...dialogProps}
			onConfirm={() => {
				return removeArticleMutate().then(() => {
					dialogProps?.onConfirm?.();
				});
			}}
			visible={dialogProps.visible && Boolean(knowledgeBaseArticle)}
		/>
	);
};
